// Forensic/medical-ready polygon comparison
// - Match Index: spatial overlap (Dice-like)
// - Alignment Quality: % boundary match after best-fit alignment (morphology)
// - Alignment Error: RMSE after alignment (px/mm, for advanced use)
// - Positional Shift: centroid distance before alignment (optional extra)

export const diffPolygonMetrics = (
  selectedPolygons,
  selectedToCompare,
  opts = {}
) => {
  // ----------------- helpers (scoped) -----------------

  const polygonArea = (pts) => {
    if (!Array.isArray(pts) || pts.length < 3) return 0;
    let s = 0;
    for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
      s += (pts[j].x + pts[i].x) * (pts[j].y - pts[i].y);
    }
    return Math.abs(s) / 2; // px²
  };

  const polygonPerimeter = (pts) => {
    if (!Array.isArray(pts) || pts.length < 2) return 0;
    let p = 0;
    for (let i = 0; i < pts.length; i++) {
      const a = pts[i];
      const b = pts[(i + 1) % pts.length];
      p += Math.hypot(a.x - b.x, a.y - b.y);
    }
    return p; // px
  };

  // Arc-length resampling: N points equally spaced along the perimeter
  const resamplePolygon = (pts, N = 128) => {
    if (!Array.isArray(pts) || pts.length < 3) return [];
    const edges = [];
    let L = 0;
    for (let i = 0; i < pts.length; i++) {
      const a = pts[i];
      const b = pts[(i + 1) % pts.length];
      const len = Math.hypot(b.x - a.x, b.y - a.y);
      edges.push({ a, b, len });
      L += len;
    }
    if (L === 0) return Array(N).fill(pts[0]);

    const step = L / N;
    const out = [];
    let ei = 0;
    let eo = edges[0];
    let acc = 0;

    for (let k = 0; k < N; k++) {
      const target = k * step;
      while (acc + eo.len < target) {
        acc += eo.len;
        ei = (ei + 1) % edges.length;
        eo = edges[ei];
      }
      const t = eo.len ? (target - acc) / eo.len : 0;
      out.push({
        x: eo.a.x + (eo.b.x - eo.a.x) * t,
        y: eo.a.y + (eo.b.y - eo.a.y) * t,
      });
    }

    return out;
  };

  const computeCentroid = (pts) => {
    if (!pts || !pts.length) return { x: 0, y: 0 };
    let sx = 0,
      sy = 0;
    for (const p of pts) {
      sx += p.x;
      sy += p.y;
    }
    return { x: sx / pts.length, y: sy / pts.length };
  };

  // Rigid best-fit (rotation + translation), then RMSE
  // This is used purely for MORPHOLOGY (not position)
  const kabschAlignAndRMSE = (A, B) => {
    const n = Math.min(A.length, B.length);
    if (!n) return { rmse: null, Bhat: [] };

    // centroids
    let ax = 0,
      ay = 0,
      bx = 0,
      by = 0;
    for (let i = 0; i < n; i++) {
      ax += A[i].x;
      ay += A[i].y;
      bx += B[i].x;
      by += B[i].y;
    }
    ax /= n;
    ay /= n;
    bx /= n;
    by /= n;

    const Ac = [];
    const Bc = [];
    for (let i = 0; i < n; i++) {
      Ac.push([A[i].x - ax, A[i].y - ay]);
      Bc.push([B[i].x - bx, B[i].y - by]);
    }

    // covariance components
    let Sxx = 0,
      Sxy = 0,
      Syx = 0,
      Syy = 0;
    for (let i = 0; i < n; i++) {
      Sxx += Bc[i][0] * Ac[i][0];
      Sxy += Bc[i][0] * Ac[i][1];
      Syx += Bc[i][1] * Ac[i][0];
      Syy += Bc[i][1] * Ac[i][1];
    }
    const det = Sxx * Syy - Sxy * Syx;
    const trace = Sxx + Syy;
    const norm = Math.hypot(trace, Sxy - Syx);
    const c = norm ? trace / norm : 1;
    const s = norm ? (Sxy - Syx) / norm : 0;
    let R = [
      [c, -s],
      [s, c],
    ];
    // reflection check
    if (det < 0) {
      R = [
        [c, s],
        [s, -c],
      ];
    }

    const Bhat = [];
    for (let i = 0; i < n; i++) {
      const x = Bc[i][0];
      const y = Bc[i][1];
      const rx = R[0][0] * x + R[0][1] * y;
      const ry = R[1][0] * x + R[1][1] * y;
      // Put aligned B back into A's centroid frame
      Bhat.push({ x: rx + ax, y: ry + ay });
    }

    let sse = 0;
    for (let i = 0; i < n; i++) {
      sse += (A[i].x - Bhat[i].x) ** 2 + (A[i].y - Bhat[i].y) ** 2;
    }
    const rmse = Math.sqrt(sse / n);
    return { rmse, Bhat };
  };

  const pointInPolygon = (pt, poly) => {
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const xi = poly[i].x,
        yi = poly[i].y;
      const xj = poly[j].x,
        yj = poly[j].y;
      const intersect =
        yi > pt.y !== yj > pt.y &&
        pt.x < ((xj - xi) * (pt.y - yi)) / (yj - yi || 1e-12) + xi;
      if (intersect) inside = !inside;
    }
    return inside;
  };

  const overlapAreaApprox = (A, B, stepPx = 1.5) => {
    if (!A.length || !B.length) return 0;

    const minX = Math.floor(
      Math.min(...A.map((p) => p.x), ...B.map((p) => p.x))
    );
    const maxX = Math.ceil(
      Math.max(...A.map((p) => p.x), ...B.map((p) => p.x))
    );
    const minY = Math.floor(
      Math.min(...A.map((p) => p.y), ...B.map((p) => p.y))
    );
    const maxY = Math.ceil(
      Math.max(...A.map((p) => p.y), ...B.map((p) => p.y))
    );

    let count = 0;
    for (let y = minY; y <= maxY; y += stepPx) {
      for (let x = minX; x <= maxX; x += stepPx) {
        const p = { x: x + stepPx / 2, y: y + stepPx / 2 };
        if (pointInPolygon(p, A) && pointInPolygon(p, B)) count++;
      }
    }
    return count * stepPx * stepPx; // px²
  };

  const bboxDiagonal = (pts) => {
    if (!pts || !pts.length) return null;
    const xs = pts.map((p) => p.x);
    const ys = pts.map((p) => p.y);
    const w = Math.max(...xs) - Math.min(...xs);
    const h = Math.max(...ys) - Math.min(...ys);
    return Math.hypot(w, h); // px
  };

  // Decision rules using Match Index + Alignment Quality + size deltas
  const decide = ({
    matchPct,
    alignmentQualityPct,
    areaPctAbs,
    periPctAbs,
  }) => {
    // ✅ Acceptable Agreement
    if (
      matchPct != null &&
      matchPct >= 95 &&
      alignmentQualityPct != null &&
      alignmentQualityPct >= 95 &&
      (areaPctAbs == null || areaPctAbs <= 5) &&
      (periPctAbs == null || periPctAbs <= 5)
    ) {
      return {
        code: "ACCEPT",
        ui: "#44BA84",
        desc: "Acceptable Agreement",
        subText: "Differences are within expected measurement variation.",
      };
    }

    // 🟥 Requires Re-Examination – clear, substantial change
    if (
      (areaPctAbs != null && areaPctAbs > 15) ||
      (periPctAbs != null && periPctAbs > 10) ||
      (matchPct != null && matchPct < 85) ||
      (alignmentQualityPct != null && alignmentQualityPct < 85)
    ) {
      return {
        code: "REEXAM",
        ui: "#D42111",
        desc: "Requires Re-Examination",
        subText:
          "Major discrepancy in size, position, or contour; independent re-examination is advised.",
      };
    }

    // 🟧 Needs Reconciliation – grey zone / moderate differences
    if (
      (areaPctAbs != null && areaPctAbs > 5 && areaPctAbs <= 15) ||
      (periPctAbs != null && periPctAbs > 5 && periPctAbs <= 10) ||
      (matchPct != null && matchPct >= 85 && matchPct < 95) ||
      (alignmentQualityPct != null &&
        alignmentQualityPct >= 85 &&
        alignmentQualityPct < 95)
    ) {
      return {
        code: "RECONCILE",
        ui: "#FFA158",
        desc: "Needs Reconciliation",
        subText:
          "Moderate differences detected; recommended for side-by-side clinical review.",
      };
    }

    // Fallback: generally acceptable if we don't have enough info to say otherwise
    return {
      code: "OK",
      ui: "#44BA84",
      desc: "Acceptable Agreement",
      subText: "Within expected variation for repeated radiographs.",
    };
  };

  // ----------------- inputs & guards -----------------

  const ref = Array.isArray(selectedPolygons) ? selectedPolygons[0] : null;
  const cmp = selectedToCompare || null;

  if (
    !ref ||
    !cmp ||
    !Array.isArray(ref.points) ||
    !Array.isArray(cmp.points)
  ) {
    return { isValid: false, reason: "Missing polygons", metrics: null };
  }

  // ---- unit consistency check ----
  const normUnit = (u) => {
    if (!u) return "";
    return String(u)
      .toLowerCase()
      .replace(/\s+/g, "")
      .replace(/(px|mm)\^?2$/, "$1²"); // px2 -> px², mm^2 -> mm²
  };

  const extractUnits = (poly) => {
    const areaUnitProp = normUnit(poly.areaUnit);
    const periUnitProp = normUnit(poly.perimeterUnit);

    // fallback: parse from "value" string like "A: 38289.78 px  (P: 324.40 px)"
    let areaUnitText = "";
    let periUnitText = "";
    if (typeof poly.value === "string") {
      const mA = poly.value.match(/A:\s*[-\d.]+\s*([a-zµ²pxmm^2]+)/i);
      const mP = poly.value.match(/\(P:\s*[-\d.]+\s*([a-zµ²pxmm^2]+)\s*\)/i);
      areaUnitText = normUnit(mA?.[1] || "");
      periUnitText = normUnit(mP?.[1] || "");
    }

    return {
      area: areaUnitProp || areaUnitText,
      peri: periUnitProp || periUnitText,
    };
  };

  const refU = extractUnits(ref);
  const cmpU = extractUnits(cmp);

  const areaUnitsDiffer = refU.area && cmpU.area && refU.area !== cmpU.area;
  const periUnitsDiffer = refU.peri && cmpU.peri && refU.peri !== cmpU.peri;

  if (areaUnitsDiffer || periUnitsDiffer) {
    return {
      isValid: false,
      reason: `Cannot compare: units differ. Area (${refU.area || "—"} vs ${
        cmpU.area || "—"
      }), Perimeter (${refU.peri || "—"} vs ${
        cmpU.peri || "—"
      }). Convert to the same unit and try again.`,
      metrics: null,
    };
  }

  // ----------------- geometry (raw px) -----------------

  const refPts = ref.points;
  const cmpPts = cmp.points;

  const Aref = polygonArea(refPts);
  const Pref = polygonPerimeter(refPts);
  const Acmp = polygonArea(cmpPts);
  const Pcmp = polygonPerimeter(cmpPts);

  const dA = Acmp - Aref;
  const dP = Pcmp - Pref;

  const dA_pct = Aref > 0 ? (dA / Aref) * 100 : null;
  const dP_pct = Pref > 0 ? (dP / Pref) * 100 : null;

  // Overlap (Dice / F1 style): 2*overlap / (A+B)
  const step = opts.overlapStepPx ?? 1.5;
  const overlap = overlapAreaApprox(refPts, cmpPts, step);
  const matchPct =
    Aref + Acmp > 0 ? ((2 * overlap) / (Aref + Acmp)) * 100 : null;

  // ----------------- alignment / morphology -----------------

  const N = opts.resampleCount ?? 128;
  const Pr = resamplePolygon(refPts, N);
  const Pc = resamplePolygon(cmpPts, N);
  const { rmse: alignShapePx } = kabschAlignAndRMSE(Pr, Pc); // shape-only error

  // centroid shift BEFORE alignment (positional change on the image)
  const cref = computeCentroid(refPts);
  const ccmp = computeCentroid(cmpPts);
  const centroidShiftPx = Math.hypot(cref.x - ccmp.x, cref.y - ccmp.y);

  // Alignment error as % of a reference size (for UI normalization)
  const alignPctOf = opts.alignmentPctOf || "bboxDiagonal";
  let denomPx = null;
  if (alignPctOf === "perimeter") denomPx = Pref || null;
  else if (alignPctOf === "maxDimension") {
    const xs = refPts.map((p) => p.x);
    const ys = refPts.map((p) => p.y);
    const w = Math.max(...xs) - Math.min(...xs);
    const h = Math.max(...ys) - Math.min(...ys);
    denomPx = Math.max(w, h) || null;
  } else {
    denomPx = bboxDiagonal(refPts) || null;
  }

  // Geometric (contour) error as % of tooth size (shape only)
  const alignErrPct =
    alignShapePx != null && denomPx ? (alignShapePx / denomPx) * 100 : null;

  // Alignment Quality: shape similarity after best-fit alignment
  // 100% = identical contour, lower values = increasing distortion
  const alignmentQualityPct =
    alignErrPct == null ? null : Math.max(0, Math.min(100, 100 - alignErrPct));

  // ----------------- unit scaling (px → mm if calibrated) -----------------

  const mmPerPx = opts.mmPerPx ?? null;
  const areaUnit = mmPerPx ? "mm²" : "px²";
  const periUnit = mmPerPx ? "mm" : "px";
  const scaleArea = mmPerPx ? mmPerPx * mmPerPx : 1;
  const scaleLen = mmPerPx ? mmPerPx : 1;

  const Aref_u = Aref * scaleArea;
  const Acmp_u = Acmp * scaleArea;
  const Pref_u = Pref * scaleLen;
  const Pcmp_u = Pcmp * scaleLen;
  const dA_u = dA * scaleArea;
  const dP_u = dP * scaleLen;
  const alignShape_u = alignShapePx != null ? alignShapePx * scaleLen : null;
  const centroidShift_u = centroidShiftPx * scaleLen;

  // ----------------- decision -----------------

  const decision = decide({
    matchPct,
    alignmentQualityPct: alignmentQualityPct, // use shape-only % we computed
    areaPctAbs: dA_pct != null ? Math.abs(dA_pct) : null,
    periPctAbs: dP_pct != null ? Math.abs(dP_pct) : null,
  });

  // ----------------- formatters -----------------

  const pctSigned = (v) =>
    v == null ? "—" : `${v >= 0 ? "+" : ""}${v.toFixed(1)}%`;
  const pctUnsigned = (v) => (v == null ? "—" : `${v.toFixed(1)}%`);
  const rawUnsigned = (v, unit = "") =>
    v == null ? "—" : `${v.toFixed(2)}${unit}`;

  const makeDeltaTooltip = (pct, type) => {
    if (pct == null) {
      return type === "area"
        ? "Change in area between the two polygons."
        : "Change in perimeter between the two polygons.";
    }
    const mag = Math.abs(pct).toFixed(1);
    if (Math.abs(pct) < 0.1) {
      return type === "area"
        ? "Areas are essentially identical between reference and compared shapes."
        : "Perimeters are essentially identical between reference and compared shapes.";
    }
    const dir = pct > 0 ? "larger" : "smaller";
    return type === "area"
      ? `Compared shape area is about ${mag}% ${dir} than the reference shape.`
      : `Compared shape perimeter is about ${mag}% ${dir} than the reference shape.`;
  };

  const matchIndexTooltip =
    "The percentage of overlapping area shared between the two polygons, indicating how similar their shapes are in size and outline. Higher values mean both polygons cover the same anatomical region.";

  const alignmentQualityTooltip =
    "Measures how similar the shape of the compared polygon is to the reference polygon after best-fit alignment, including curvature, bend, tilt, rotation and distortion. Higher values mean both polygons follow the same anatomical contour, even if they have moved slightly on the image.";

  const positionalShiftTooltip =
    "Distance between the centres (centroids) of both polygons; larger values indicate greater movement of the feature between examinations.";

  const alignmentErrorTooltip =
    "Average distance between corresponding boundary points after best-fit alignment; smaller values indicate closer geometric agreement in shape.";

  // ----------------- return (UI-ready) -----------------

  const areaTooltip = makeDeltaTooltip(dA_pct, "area");
  const periTooltip = makeDeltaTooltip(dP_pct, "perimeter");

  return {
    isValid: true,
    metrics: {
      area: {
        ref: Aref_u,
        cmp: Acmp_u,
        delta: dA_u,
        deltaPct: dA_pct,
        unit: areaUnit,
        displayPct: pctSigned(dA_pct),
        displayRaw: rawUnsigned(dA_u, ` ${areaUnit}`),
        tooltip: areaTooltip,
      },
      perimeter: {
        ref: Pref_u,
        cmp: Pcmp_u,
        delta: dP_u,
        deltaPct: dP_pct,
        unit: periUnit,
        displayPct: pctSigned(dP_pct),
        displayRaw: rawUnsigned(dP_u, ` ${periUnit}`),
        tooltip: periTooltip,
      },

      // Spatial overlap of the two shapes on the image
      matchIndexPct: matchPct,
      matchIndexDisplay: pctUnsigned(matchPct),
      matchIndexTooltip,

      // Morphological change after best-fit alignment (shape-only)
      alignmentError: {
        value: alignShape_u, // px/mm
        unit: periUnit,
        pct: alignErrPct, // % of reference size
        displayPct: pctUnsigned(alignErrPct),
        displayRaw: rawUnsigned(alignShape_u, ` ${periUnit}`),
        tooltip: alignmentErrorTooltip,
      },

      // Alignment Quality: 100% = perfect contour match after alignment
      alignmentQuality: {
        pct: alignmentQualityPct,
        displayPct: pctUnsigned(alignmentQualityPct),
        tooltip: alignmentQualityTooltip,
      },

      // How far the feature moved on the radiograph (position change)
      positionalShift: {
        value: centroidShift_u,
        unit: periUnit,
        displayRaw: rawUnsigned(centroidShift_u, ` ${periUnit}`),
        tooltip: positionalShiftTooltip,
      },
    },
    decision, // { code, ui, desc, subText }
  };
};
